import { TRUCK_COMPLIANCE_STATUSES, TRUCK_STATUSES, type TruckListQuery } from '@chukua/common';
import { Tag, Typography, type TableColumnsType } from 'antd';
import { Link } from 'react-router';
import type { ListQueryControls } from '../../lib/use-list-query';
import { COMPLIANCE_LABELS } from './compliance-labels';
import { TRUCK_STATUS_COLORS, TRUCK_STATUS_LABELS } from './truck-status-labels';
import type { TruckListItem } from './trucks.api';

interface BuildTruckColumnsArgs {
  list: Pick<ListQueryControls<TruckListQuery>, 'sortOrderFor' | 'filteredValueFor'>;
  classOptions: { text: string; value: string }[];
}

const COMPLIANCE_FILTERS = TRUCK_COMPLIANCE_STATUSES.map((status) => ({ text: COMPLIANCE_LABELS[status].label, value: status }));
const STATUS_FILTERS = TRUCK_STATUSES.map((status) => ({ text: TRUCK_STATUS_LABELS[status], value: status }));

/** Sorting and filtering run on the server: each column's `key` is its `sort` field or filter param in `truckListQuerySchema`. */
export function buildTruckColumns({ list, classOptions }: BuildTruckColumnsArgs): TableColumnsType<TruckListItem> {
  return [
    {
      title: 'Registration',
      dataIndex: 'registration',
      key: 'registration',
      sorter: true,
      sortOrder: list.sortOrderFor('registration'),
      render: (registration: string, truck) => (
        <Link className="font-medium text-link hover:underline" to={`/fleet/${truck.id}`}>
          <Typography.Text code>{registration}</Typography.Text>
        </Link>
      ),
    },
    {
      title: 'Class',
      dataIndex: 'classCode',
      key: 'classCode',
      filters: classOptions,
      filteredValue: list.filteredValueFor('classCode'),
    },
    {
      title: 'Make / model',
      key: 'make',
      sorter: true,
      sortOrder: list.sortOrderFor('make'),
      render: (_, truck) => `${truck.make} ${truck.model}`,
    },
    { title: 'Capacity (t)', dataIndex: 'capacityTonnes', key: 'capacityTonnes' },
    {
      title: 'Status',
      key: 'status',
      filters: STATUS_FILTERS,
      filteredValue: list.filteredValueFor('status'),
      render: (_, truck) => <Tag color={TRUCK_STATUS_COLORS[truck.status]}>{TRUCK_STATUS_LABELS[truck.status]}</Tag>,
    },
    {
      title: 'Compliance',
      key: 'compliance',
      filters: COMPLIANCE_FILTERS,
      filteredValue: list.filteredValueFor('compliance'),
      render: (_, truck) => <Tag color={COMPLIANCE_LABELS[truck.compliance].color}>{COMPLIANCE_LABELS[truck.compliance].label}</Tag>,
    },
    {
      title: '',
      key: 'actions',
      render: (_, truck) => (
        <Link className="text-sm font-medium text-link hover:underline" to={`/fleet/${truck.id}`}>
          View
        </Link>
      ),
    },
  ];
}
